import { Stack, Text } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'

import { RPCRefresh } from '@/client/rpc'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function useCohostRequestModal(): [
  ({
    subjectUserId,
    sessionOrSeriesId,
    isSession,
  }: {
    subjectUserId?: number | null
    sessionOrSeriesId?: number | null
    isSession: boolean
  }) => JSX.Element,
  () => void,
] {
  const [showModal, setShowModal] = useState(false)

  const CohostRequestModal = ({
    subjectUserId = null,
    sessionOrSeriesId = null,
    isSession,
  }: {
    subjectUserId?: number | null
    sessionOrSeriesId?: number | null
    isSession: boolean
  }) => {
    const toast = useToaster()
    const sessionOrSeries = isSession ? 'session' : 'series'
    const requestCohostInputs: FormInputType[] = useMemo(
      () => [
        {
          label: 'Write a message (optional)',
          type: 'textarea',
          name: 'reason',
          placeholder: 'Let them know why you are interested in tutoring together',
          required: false,
          size: 6,
        },
      ],
      []
    )

    const handleSubmit = useCallback(
      async (values: FormValues) => {
        if (subjectUserId == null) {
          return
        }
        if (isSession) {
          await RPCRefresh.requestSessionHost({
            sessionId: sessionOrSeriesId ?? 0,
            requestMessage: `${values.reason}`,
          })
        } else {
          await RPCRefresh.requestSeriesHost({
            seriesId: sessionOrSeriesId ?? 0,
            requestMessage: `${values.reason}`,
          })
        }
        toast({
          title: `You requested to co-host this ${sessionOrSeries}.`,
          status: 'success',
        })
        setShowModal(false)
      },
      [toast, subjectUserId, sessionOrSeriesId, isSession, sessionOrSeries]
    )

    return (
      <ModalV3
        size="2xl"
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Request to Co-host"
        body={() => (
          <Stack spacing="18px">
            <Text>
              Want to co-host this {sessionOrSeries}? Request to co-host this {sessionOrSeries}
              &#32;with the main tutor and we&apos;ll let them know! As a co-host, you&apos;ll
              receive volunteer hours, Schoolhouse Points (SP), and feedback for any{' '}
              {sessionOrSeries}&#32;you attend. Meanwhile, we encourage you to reach out to the main
              host on via Schoolhouse chat or Slack if you haven&apos;t done so already.
            </Text>
            <Text>
              Note: Co-hosting is for tutors who are working together to plan and create a{' '}
              {sessionOrSeries}. If it&apos;s discovered that this feature is being abused, you will
              be reported.
            </Text>
            <Form name="CohostRequest" blocks={requestCohostInputs} onSubmit={handleSubmit} />
          </Stack>
        )}
      />
    )
  }
  return [CohostRequestModal, () => setShowModal(true)]
}
